/**
 * GLSL functions for rendering design material SDF patterns using distance field raymarching.
 * Computes similar output variables as parallaxMap.ts.
 * @module
 */
import degreeToRadian from "common/utils/degreeToRadian";
import * as THREE from "three";
import { MAX_SDF_PATTERNS } from "../designMaterialDefaults";
import { sdfCompileTimeSettingsMatch, getCellZBoundsRelativeToTop } from "../designMaterialParams";
import type {
  DmRoundSidesMode,
  DmTransparencyMode,
  SdfCommonParamsPreprocessed,
  ShaderSdfPattern,
} from "../designMaterialTypes";
import { v2Uniform, fUniform, v4Uniform, v3Uniform } from "../uniformHelpers";
import { hash21Declaration, OUTSIDE_RANDOM_MASK_VALUE } from "./utils";

const sdfDefines = `
#ifndef NORMAL_WIDTH
#define NORMAL_WIDTH 0.1
#endif
#ifndef MARCH_ITER
#define MARCH_ITER (18)
#endif
#ifndef SHADOW_ITER
#define SHADOW_ITER (16)
#endif
`;

function getSdfVarDeclarations() {
  return `
  uniform float sdfDepth;
  uniform float useSdfShadows;
  uniform float sdfAoStrength;
  uniform float sdfAoTolerance;
  uniform vec4 tileIDSeed;
  
  #ifdef SDF_REPEAT
  uniform vec2 sdfRepeat;  // Repeat interval in multiples of sdfDistanceX[0] / sdfDistanceY[0].
  #endif
  
  #ifdef USE_SDF_SIMD
  uniform vec4 sdfOffsetX;
  uniform vec4 sdfOffsetY;
  uniform vec4 sdfSizeX;
  uniform vec4 sdfSizeY;
  uniform vec4 sdfSizeZ;
  uniform vec4 sdfDistanceX;
  uniform vec4 sdfDistanceY;
  uniform vec4 sdfBevelsX;
  uniform vec4 sdfBevelsY;
  uniform vec4 sdfBevelsZ;
  #else
  uniform vec2 sdfOffset;
  uniform vec2 sdfOffset2;
  uniform vec2 sdfOffset3;
  uniform vec2 sdfOffset4;
  uniform vec3 sdfSize;
  uniform vec3 sdfSize2;
  uniform vec3 sdfSize3;
  uniform vec3 sdfSize4;
  uniform vec2 sdfDistance;
  uniform vec2 sdfDistance2;
  uniform vec2 sdfDistance3;
  uniform vec2 sdfDistance4;
  uniform vec3 sdfBevels;
  uniform vec3 sdfBevels2;
  uniform vec3 sdfBevels3;
  uniform vec3 sdfBevels4;
  #endif // USE_SDF_SIMD
  uniform int sdfSlopeAxis;
  uniform vec4 sdfRotation;
  uniform vec4 sdfSlope;
  uniform vec4 sdfOffsetZ;
  uniform vec4 varyHeightStr;
  uniform vec4 cutXfromYStr;
  uniform vec4 roundStrength;
  
  // This is a bit dirty - we keep these sdf shader variables here so that every mapSdf call is not burdened with extra parameters.
  vec4 boxD;
  vec4 idX;
  vec4 idY;

  // We use this continuous function for pseudorandom noise instead of fract(), since the discontinuities in fract() make it sensitive to computation variance.
  // The variance has manifested as AO artifacts on Mac M1 (cell height would get evaluated differently when positioning the cell and when computing AO)
  float triangleWave(float value) {
    return abs(mod(value, 2.0) - 1.0);
  }

  vec4 triangleWaveSIMD(vec4 value) {
    return abs(mod(value, 2.0) - 1.0);
  }

  float square( vec2 p, vec2 size ) {
    vec2 d = abs(p) - size;
    return length(max(d,0.0)) + min(max(d.x,d.y),0.0);
  }
  
  float box( vec3 p, vec3 size ) {
    vec3 d = abs(p) - size;
    return min(max(d.x,max(d.y,d.z)),0.0) + length(max(d,0.0));
  }
  
  #ifdef USE_SDF_SIMD
  vec4 squareSIMD( vec4 absPX, vec4 absPY, vec4 sizeX, vec4 sizeY ) {
    vec4 dX = absPX - sizeX;
    vec4 dY = absPY - sizeY;
    vec4 maxX = max(dX,0.0);
    vec4 maxY = max(dY,0.0);
    return vec4(length(vec2(maxX.x, maxY.x)), length(vec2(maxX.y, maxY.y)), length(vec2(maxX.z, maxY.z)), length(vec2(maxX.w, maxY.w))) + min(max(dX,dY),0.0);
  }
  
  vec4 boxSIMD( vec4 absPX, vec4 absPY, vec4 absPZ, vec4 sizeX, vec4 sizeY, vec4 sizeZ ) {
    vec4 dX = absPX - sizeX;
    vec4 dY = absPY - sizeY;
    vec4 dZ = absPZ - sizeZ;
    vec4 maxX = max(dX,0.0);
    vec4 maxY = max(dY,0.0);
    vec4 maxZ = max(dZ,0.0);
    return min(max(dX,max(dY,dZ)),0.0) + vec4(length(vec3(maxX.x, maxY.x, maxZ.x)), length(vec3(maxX.y, maxY.y, maxZ.y)), length(vec3(maxX.z, maxY.z, maxZ.z)), length(vec3(maxX.w, maxY.w, maxZ.w)));
  }
  
  #endif
  
  float boxR( vec3 p, vec3 size, vec2 scale, float r ) {
    vec3 d = abs(p) - size;
    d /= vec3(scale, 1);
    d += r;
    return min(max(d.x,max(d.y,d.z)),0.0) + length(max(d,0.0)) - r;
  }
  
  mat2 rot(in float a) {
    float c = cos(a), s = sin(a);
    return mat2(c, s, -s, c);
  }

  ${hash21Declaration}
  `;
}

function getEvalSDFBoxCode(index: number, roundSides: DmRoundSidesMode, useBevels: boolean, addToRnSeed: string) {
  const vectorSub = [".x", ".y", ".z", ".w"][index - 1];
  const indexStr = index === 1 ? "" : "" + index;

  // Note that some of the rounded SDFs estimate distance as something between Chebyshev distance and Euclidean distance.
  let boxDChunk = `float singleBoxD = box(p, sdfSize - vec3(cut,0,0));`;
  if (roundSides === "roundSides") {
    boxDChunk = `float singleBoxD = box(p, sdfSize - vec3(cut,0,0) - roundStrength) - roundStrength;`;
  } else if (roundSides === "roundY") {
    boxDChunk = `float singleBoxD = abs(p.y) - sdfSize.y;
    singleBoxD = max( singleBoxD, square(p.xz, vec2(sdfSize.x - roundStrength, sdfSize.z - roundStrength)) - roundStrength );`;
  } else if (roundSides === "roundX") {
    boxDChunk = `float singleBoxD = abs(p.x) - sdfSize.x;
    singleBoxD = max( singleBoxD, square(p.yz, vec2(sdfSize.y - roundStrength, sdfSize.z - roundStrength)) - roundStrength );`;
  } else if (roundSides === "roundZ") {
    boxDChunk = `float singleBoxD = abs(p.z) - sdfSize.z;
    singleBoxD = max( singleBoxD, square(p.yx, vec2(sdfSize.y - roundStrength, sdfSize.x - roundStrength)) - roundStrength );`;
  }

  let bevelsChunk = "";
  if (useBevels) {
    bevelsChunk = `// bevel x/y
    float bevel = max(abs(p.x) + sdfBevels.x, abs(p.y) + sdfBevels.y) + p.z;

    // bevel sides
    bevel = max(bevel, abs(p.x) + abs(p.y) + sdfBevels.z);

    singleBoxD = max(singleBoxD, bevel * 0.7071);`;
  }

  return `
{

  idX = floor(p.x/sdfDistance.x);
  idY = floor(p.y/sdfDistance.y);

  p.xy = p.xy - sdfDistance * (vec2(idX, idY) + 0.5);

  #ifdef SDF_REPEAT
    // Make the SDF random variability repeat over a certain number of tiles.
    idX = mod(idX, sdfRepeat.x);
    idY = mod(idY, sdfRepeat.y);
  #endif

  float zOffset = sdfOffsetZ;
  zOffset += (triangleWave((idY * 0.09 + idX * 0.17) * (idY - 21.1) * (idX ${addToRnSeed}) * 6.38) - 0.5) * varyHeightStr;

  float cut = p.y*cutXfromYStr;

  p.z += zOffset;

  if (sdfSlopeAxis == 0) {
    p.yz *= rot( sdfSlope );
  } else {
    p.yx *= rot( sdfSlope );
  }

  ${boxDChunk}

  ${bevelsChunk}

  d = min(singleBoxD, d);

  boxD${vectorSub} = singleBoxD;
}`
    .replace(/varyHeightStr/g, "varyHeightStr" + vectorSub)
    .replace(/sdfSlope /g, "sdfSlope" + vectorSub)
    .replace(/cutXfromYStr/g, "cutXfromYStr" + vectorSub)
    .replace(/roundStrength/g, "roundStrength" + vectorSub)
    .replace(/sdfOffsetZ/g, "sdfOffsetZ" + vectorSub)
    .replace(/idX/g, "idX" + vectorSub)
    .replace(/idY/g, "idY" + vectorSub)
    .replace(/sdfDistance/g, "sdfDistance" + indexStr)
    .replace(/sdfSize/g, "sdfSize" + indexStr)
    .replace(/sdfBevels/g, "sdfBevels" + indexStr)
    .replace(/\bp\b/g, "p" + indexStr);
}

function getEvalSDFSIMDCode(roundSides: DmRoundSidesMode) {
  // Note that some of the rounded SDFs estimate distance as something between Chebyshev distance and Euclidean distance.
  let boxDChunk = `boxD = boxSIMD(pX, pY, abs(pZ), sdfSizeX - cut, sdfSizeY, sdfSizeZ);`;
  if (roundSides === "roundSides") {
    boxDChunk = `boxD = boxSIMD(pX, pY, abs(pZ), sdfSizeX - cut - roundStrength, sdfSizeY - roundStrength, sdfSizeZ - roundStrength) - roundStrength;`;
  } else if (roundSides === "roundY") {
    boxDChunk = `pZ = abs(pZ);
    boxD = pY - sdfSizeY;
    boxD = max( boxD, squareSIMD(pX, pZ, sdfSizeX - roundStrength, sdfSizeZ - roundStrength) - roundStrength );`;
  } else if (roundSides === "roundX") {
    boxDChunk = `pZ = abs(pZ);
    boxD = pX - sdfSizeX;
    boxD = max( boxD, squareSIMD(pY, pZ, sdfSizeY - roundStrength, sdfSizeZ - roundStrength) - roundStrength );`;
  } else if (roundSides === "roundZ") {
    boxDChunk = `pZ = abs(pZ);
    boxD = pZ - sdfSizeZ;
    boxD = max( boxD, squareSIMD(pY, pX, sdfSizeY - roundStrength, sdfSizeX - roundStrength) - roundStrength );`;
  }

  return `
{

  idX = floor(pX / sdfDistanceX);
  idY = floor(pY / sdfDistanceY);

  pX = pX - sdfDistanceX * (idX + 0.5);
  pY = pY - sdfDistanceY * (idY + 0.5);

  #ifdef SDF_REPEAT
    // Make the SDF random variability repeat over a certain number of tiles.
    idX = mod(idX, sdfRepeat.x);
    idY = mod(idY, sdfRepeat.y);
  #endif

  vec4 zOffset = sdfOffsetZ;
  const vec4 addToRnSeed = vec4(31.4, 42.2, 55.2, 59.2);
  zOffset += (triangleWaveSIMD((idY * 0.09 + idX * 0.17) * (idY - 21.1) * (idX - addToRnSeed) * 6.38) - 0.5) * varyHeightStr;

  vec4 cut = pY*cutXfromYStr;

  pZ += zOffset;

  vec4 cosSdfSlope = cos(sdfSlope);
  vec4 sinSdfSlope = sin(sdfSlope);

  vec4 pY2;

  if (sdfSlopeAxis == 0) {
    pY2 = cosSdfSlope * pY + sinSdfSlope * pZ;
    pZ = -sinSdfSlope * pY + cosSdfSlope * pZ;
  } else {
    pY2 = cosSdfSlope * pY - sinSdfSlope * pX;
    pX = sinSdfSlope * pY + cosSdfSlope * pX;
  }

  pX = abs(pX);
  pY = abs(pY2);

  // bevel x/y
  vec4 bevel = max(pX + sdfBevelsX, pY + sdfBevelsY) + pZ;
  
  // bevel sides
  bevel = max(bevel, pX + pY + sdfBevelsZ);

  ${boxDChunk}

  boxD = max(boxD, bevel * 0.7071);

  // Note that in case patterns are not enabled, we just set their size to zero instead of disabling them in the shader.
  d = min(min(boxD.x, boxD.y), min(boxD.z, boxD.w));
}`;
}

function getMapSdfNonSimdDefinition(
  nameSuffix: string,
  roundSides: DmRoundSidesMode,
  roundSides2: DmRoundSidesMode,
  roundSides3: DmRoundSidesMode,
  roundSides4: DmRoundSidesMode,
  useBevels: boolean,
  useBevels2: boolean,
  useBevels3: boolean,
  useBevels4: boolean,
) {
  return `
float mapSdf${nameSuffix}( vec3 p )	{
  float plane = p.z + sdfDepth;
  float d = plane;

  vec3 p2 = vec3(p.xy*rot(sdfRotation.y) + sdfOffset2, p.z);
  vec3 p3 = vec3(p.xy*rot(sdfRotation.z) + sdfOffset3, p.z);
  vec3 p4 = vec3(p.xy*rot(sdfRotation.w) + sdfOffset4, p.z);
  p.xy *= rot(sdfRotation.x);
  p.xy += sdfOffset;

  ${getEvalSDFBoxCode(1, roundSides, useBevels, "- 31.4")}

#ifdef SECOND_PATTERN
  ${getEvalSDFBoxCode(2, roundSides2, useBevels2, "- 42.2")}
#endif
#ifdef THIRD_PATTERN
  ${getEvalSDFBoxCode(3, roundSides3, useBevels3, "- 55.2")}
#endif
#ifdef FOURTH_PATTERN
  ${getEvalSDFBoxCode(4, roundSides4, useBevels4, "- 59.2")}
#endif

  return d;
}
`;
}

function getMapSdfSimdDefinition(nameSuffix: string, roundSides: DmRoundSidesMode) {
  return `
float mapSdf${nameSuffix}( vec3 p )	{
  float plane = p.z + sdfDepth;
  vec2 p2 = p.xy * rot(sdfRotation.y);
  vec2 p3 = p.xy * rot(sdfRotation.z);
  vec2 p4 = p.xy * rot(sdfRotation.w);
  p.xy *= rot(sdfRotation.x);

  vec4 pZ = vec4(p.z);
  vec4 pX = vec4(p.x, p2.x, p3.x, p4.x);
  vec4 pY = vec4(p.y, p2.y, p3.y, p4.y) + sdfOffsetY;

  // Doing this (adding 0.00000001) mostly works around a bug on Apple M1 hardware seen in ScaleShingle materials.
  pX += sdfOffsetX + 0.00000001;

  float d;
  ${getEvalSDFSIMDCode(roundSides)}

  return min(d, plane);
}
`;
}

function getSdfBasedFunctions(nameSuffix: string) {
  return `
vec3 getNormal${nameSuffix}( in vec3 pos ) {
    vec3 eps = vec3( NORMAL_WIDTH, 0.0, 0.0 );
    return normalize( vec3(mapSdf${nameSuffix}( pos+eps.xyy ) - mapSdf${nameSuffix}( pos-eps.xyy ),
                            mapSdf${nameSuffix}( pos+eps.yxy ) - mapSdf${nameSuffix}( pos-eps.yxy ),
                            mapSdf${nameSuffix}( pos+eps.yyx ) - mapSdf${nameSuffix}( pos-eps.yyx ) ) );

}

float tgtMarch${nameSuffix}( vec2 uv, vec3 rd ) {
    vec3 ro = vec3(uv, 0.0);
    const float far = 10.0;
    float precis = 0.0001;
    float h = precis*2.0;
    float travel = -0.0001;
    boxD = vec4(sdfDepth * 2.0); // This initialization is needed for the non-SIMD path, since it might not set all boxD values.
    for( int i=0; i<MARCH_ITER; i++ ) {
        if( abs(h)<precis || travel>far ) break;
        travel += h;
        float res = mapSdf${nameSuffix}(ro + rd*travel)*0.65;
        h = res;
    }
    float plane = rd.z * travel + sdfDepth;
    float d = plane;
    vec2 tileIDBase;
    if (boxD.x < d) {
      tileIDBase = vec2(idX.x, idY.x) + tileIDSeed.x;
      d = boxD.x;
      sdfPatternIDMask = 1;
    }
    if (boxD.y < d) {
      tileIDBase = vec2(idX.y, idY.y) + tileIDSeed.y;
      d = boxD.y;
      sdfPatternIDMask = 2;
    }
    if (boxD.z < d) {
      tileIDBase = vec2(idX.z, idY.z) + tileIDSeed.z;
      d = boxD.z;
      sdfPatternIDMask = 4;
    }
    if (boxD.w < d) {
      tileIDBase = vec2(idX.w, idY.w) + tileIDSeed.w;
      d = boxD.w;
      sdfPatternIDMask = 8;
    }
    mainRandomMask = d < plane ? hash21(tileIDBase) * 0.5 + 0.5 : ${OUTSIDE_RANDOM_MASK_VALUE.toFixed(1)};
    return travel;
}

float tgtMarchMask${nameSuffix}( vec2 uv, vec3 rd ) {
    vec3 ro = vec3(uv, 0.0);
    const float far = 10.0;
    float precis = 0.0001;
    float h = precis*2.0;
    float travel = 0.0;
    boxD = vec4(sdfDepth * 2.0); // This initialization is needed for the non-SIMD path, since it might not set all boxD values.
    for( int i=0; i<11; i++ ) {
        if( abs(h)<precis || travel>far ) break;
        travel += h;
        float res = mapSdf${nameSuffix}(ro + rd*travel);
        h = res;
    }
    float plane = rd.z * travel + sdfDepth;
    float d = plane;
    vec2 tileIDBase;
    if (boxD.x < d) {
      tileIDBase = vec2(idX.x, idY.x) + tileIDSeed.x;
      d = boxD.x;
      sdfPatternIDMask = 1;
    }
    if (boxD.y < d) {
      tileIDBase = vec2(idX.y, idY.y) + tileIDSeed.y;
      d = boxD.y;
      sdfPatternIDMask = 2;
    }
    if (boxD.z < d) {
      tileIDBase = vec2(idX.z, idY.z) + tileIDSeed.z;
      d = boxD.z;
      sdfPatternIDMask = 4;
    }
    if (boxD.w < d) {
      tileIDBase = vec2(idX.w, idY.w) + tileIDSeed.w;
      d = boxD.w;
      sdfPatternIDMask = 8;
    }
    mainRandomMask = d < plane ? hash21(tileIDBase) * 0.5 + 0.5 : ${OUTSIDE_RANDOM_MASK_VALUE.toFixed(1)};
    return travel;
}

float getShadow${nameSuffix}( vec3 origin, vec3 toLight)
{
    float shadow = 1.0;
    float travel = 0.00;
    float prev = 1e10;
    float hardness = 16.0;
    float far = 10.;
    for( int i=0; i<SHADOW_ITER; i++ )
    {
        float dst = mapSdf${nameSuffix}( origin + toLight*travel );
        float tri = dst*dst/(2.0*prev);
        shadow = min( shadow, hardness*sqrt(dst*dst - tri*tri)/max(0.0, travel - tri) );
        travel += dst;
        if( shadow<0.0001 || travel>far ) break;
        prev = dst;
    }
    return clamp( shadow, 0.0, 1.0 );
}

//#define CHEAPER_AO

#ifdef CHEAPER_AO
float curv${nameSuffix}(in vec3 p, in float w)
{
    vec2 e = vec2(-1., 1.)*w;
    float t1 = mapSdf${nameSuffix}(p + e.yxx), t2 = mapSdf${nameSuffix}(p + e.xxy);
    float t3 = mapSdf${nameSuffix}(p + e.xyx), t4 = mapSdf${nameSuffix}(p + e.yyy);
    return 0.25/e.y*(t1 + t2 + t3 + t4 - 4. * mapSdf${nameSuffix}(p));
}
#else
float curv${nameSuffix}(in vec3 p, in float w)
{
    vec3 e = vec3(w*2.0, 0, 0);
    float t1 = mapSdf${nameSuffix}(p + e.xyy), t2 = mapSdf${nameSuffix}(p - e.xyy);
    float t3 = mapSdf${nameSuffix}(p + e.yxy), t4 = mapSdf${nameSuffix}(p - e.yxy);
    float t5 = mapSdf${nameSuffix}(p + e.yyx), t6 = mapSdf${nameSuffix}(p - e.yyx);
    return 0.25/e.x*(t1 + t2 + t3 + t4 + t5 + t6 - 6.0*mapSdf${nameSuffix}(p));
}
#endif
`;
}

export function getSDFDeclarations(patterns: [ShaderSdfPattern, ShaderSdfPattern, ShaderSdfPattern, ShaderSdfPattern]) {
  let mapSdfDefinition;
  const useSimd = sdfCompileTimeSettingsMatch(patterns);
  if (useSimd) {
    mapSdfDefinition = getMapSdfSimdDefinition("", patterns[0].roundSidesMode);
  } else {
    mapSdfDefinition = getMapSdfNonSimdDefinition(
      "",
      patterns[0].roundSidesMode,
      patterns[1].roundSidesMode,
      patterns[2].roundSidesMode,
      patterns[3].roundSidesMode,
      patterns[0].useBevels,
      patterns[1].useBevels,
      patterns[2].useBevels,
      patterns[3].useBevels,
    );
  }
  return sdfDefines + getSdfVarDeclarations() + mapSdfDefinition + getSdfBasedFunctions("");
}

export const preLightSdfFull = (initUVSpaceTanRay: string, applyDetailTgtNormal: string) => {
  return `
${initUVSpaceTanRay}
vec3 tanLight = directionalLights[0].direction * dmTbn;

float travel = tgtMarch(vUv, tanRay);

// Compute SDF mask values - this is done even if they are not used.
float depthForMask = 1.0 - (-tanRay.z * travel) / (sdfDepth);

vec3 detailTgtNormal = getNormal(vec3(vUv, 0.0) + tanRay*travel);
${applyDetailTgtNormal}

#if NUM_DIR_LIGHTS > 0
if ( useSdfShadows > 0.5 ) {
  detailSelfShadow = getShadow(vec3( vUv,0 ) + tanRay*(travel - 0.005), tanLight);
}
#endif
if (sdfAoStrength > 0.0) {
  detailSelfAO = curv(vec3( vUv,0 ) + tanRay*( travel - 0.0005 ), sdfAoTolerance );
  detailSelfAO = clamp(detailSelfAO, -2.0, 0.0)*sdfAoStrength + 1.0;
}
detailSelfShadow = clamp(detailSelfShadow, 0.,1.);

uvWithParallax = vUv + tanRay.xy*travel;
`;
};

export const preLightSdfMask = (initUVSpaceTanRay: string, _applyDetailTgtNormal: string) => {
  return `
#ifdef USE_SDF_MASK_OR_RANDOM
  ${initUVSpaceTanRay}
  float travel = tgtMarchMask(vUv, tanRay);
  float depthForMask = 1.0 - (-tanRay.z * travel) / (sdfDepth);
#else
  float depthForMask = 0.0;
#endif
  uvWithParallax = vUv;
`;
};

const SDF_SHADER_TILE_ID_OFFSETS = [0.0, 111.21, -74.64, 214.72];

// Map random seed indices >= 4 to unused random seed indices <= 4 for the SDF shader.
function limitRandomSeedIndices(shaderSdfPatterns: ShaderSdfPattern[]): number[] {
  const shaderRandomSeedIndices = shaderSdfPatterns.map((pattern) => pattern.maskRandomSeedIndex);
  const usedRandomSeedIndices = Array(MAX_SDF_PATTERNS).fill(false);
  for (let i = 0; i < shaderSdfPatterns.length; ++i) {
    usedRandomSeedIndices[shaderSdfPatterns[i].maskRandomSeedIndex] = true;
  }
  const randomSeedIndexMapping = Array(MAX_SDF_PATTERNS);
  for (let i = SDF_SHADER_TILE_ID_OFFSETS.length; i < usedRandomSeedIndices.length; ++i) {
    if (usedRandomSeedIndices[i]) {
      for (let j = 0; j < SDF_SHADER_TILE_ID_OFFSETS.length; ++j) {
        if (!usedRandomSeedIndices[j]) {
          randomSeedIndexMapping[i] = j;
          usedRandomSeedIndices[j] = true;
          break;
        }
      }
      console.assert(randomSeedIndexMapping[i] !== undefined);
    }
  }
  for (let i = 0; i < shaderSdfPatterns.length; ++i) {
    const oldIndex = shaderSdfPatterns[i].maskRandomSeedIndex;
    if (oldIndex >= SDF_SHADER_TILE_ID_OFFSETS.length) {
      shaderRandomSeedIndices[i] = randomSeedIndexMapping[oldIndex];
    }
  }
  return shaderRandomSeedIndices;
}

// Fit zSize so that it will include the rounding.
function zSizeFitRounding(pattern: ShaderSdfPattern) {
  if (
    pattern.roundSidesMode === "roundSides" ||
    pattern.roundSidesMode === "roundX" ||
    pattern.roundSidesMode === "roundY"
  ) {
    const minStraightSection = Math.max(pattern.zSize - pattern.roundStrength, 0);
    return Math.max(pattern.roundStrength * 2 + minStraightSection, pattern.zSize);
  }
  return pattern.zSize;
}

function getZOffsetForShader(
  minZOffset: number,
  pattern: ShaderSdfPattern,
  sdfCommon: SdfCommonParamsPreprocessed,
): number {
  // Each SDF tile uses the corner closest to the surface as its pivot point when applying slope rotation along the X axis.
  // The top of the outermost SDF box is aligned with the material surface.

  // In case slope axis is Z, then the box is simply rotated around its center.
  const slope = sdfCommon.slopeAxis === "X" ? pattern.slope : 0;
  const zOffsetFromTop = sdfCommon.depth - pattern.zSize;

  const shaderZSize = zSizeFitRounding(pattern);

  return (
    zOffsetFromTop -
    minZOffset +
    Math.cos(degreeToRadian(slope)) * shaderZSize * 0.5 +
    Math.sin(Math.abs(degreeToRadian(slope))) * Math.min(pattern.ySize, pattern.yDistance) * 0.5
  );
}

function getYOffsetForShader(pattern: ShaderSdfPattern, sdfCommon: SdfCommonParamsPreprocessed): number {
  const slope = sdfCommon.slopeAxis === "X" ? pattern.slope : 0;
  return (
    pattern.yOffset -
    Math.sin(degreeToRadian(slope)) * pattern.zSize * 0.5 -
    Math.sign(slope) * (1.0 - Math.cos(degreeToRadian(slope))) * Math.min(pattern.ySize, pattern.yDistance) * 0.5
  );
}

const bevelMix = (distY: number, xSize: number, zSize: number) => distY * Math.SQRT2 - xSize * 0.5 - zSize * 0.5;
const sdfBevelsXForShader = (pattern) =>
  bevelMix(
    pattern.useBevels ? pattern.bevelDistanceY : -pattern.cutXfromYScale * pattern.ySize,
    Math.min(pattern.xSize, pattern.xDistance),
    pattern.zSize,
  );
const sdfBevelsYForShader = (pattern) =>
  bevelMix(pattern.useBevels ? pattern.bevelDistanceX : 0.0, Math.min(pattern.ySize, pattern.yDistance), pattern.zSize);
const sdfBevelsZForShader = (pattern) =>
  bevelMix(
    pattern.useBevels ? pattern.bevelDistanceZ : -pattern.cutXfromYScale * pattern.ySize,
    Math.min(pattern.xSize, pattern.xDistance),
    Math.min(pattern.ySize, pattern.yDistance),
  );

export function setSdfUniforms(
  transparency: DmTransparencyMode,
  sdfCommonParams: SdfCommonParamsPreprocessed,
  shaderSdfPatterns: [ShaderSdfPattern, ShaderSdfPattern, ShaderSdfPattern, ShaderSdfPattern],
  uniforms: { [name: string]: any },
  useSdfSIMD: boolean,
): number {
  const [minZ, maxZ] = getCellZBoundsRelativeToTop(sdfCommonParams, shaderSdfPatterns);

  const sdfTransparent = transparency === "sdfTransparent" || transparency === "metalScreen";
  uniforms.sdfDepth = fUniform(Math.max(sdfTransparent ? maxZ : sdfCommonParams.depth, 0.001) - minZ);
  const depthForMaskMultiplier = uniforms.sdfDepth.value / sdfCommonParams.depth;

  uniforms.useSdfShadows = fUniform(sdfCommonParams.useShadows ? 1 : 0);
  uniforms.sdfAoStrength = fUniform(sdfCommonParams.useAO ? sdfCommonParams.aoStrength : 0);
  uniforms.sdfAoTolerance = fUniform(sdfCommonParams.aoTolerance);

  const randomSeedIndices: number[] = limitRandomSeedIndices(shaderSdfPatterns);

  uniforms.tileIDSeed = v4Uniform(
    SDF_SHADER_TILE_ID_OFFSETS[randomSeedIndices[0]] + sdfCommonParams.randomMaskSeed,
    SDF_SHADER_TILE_ID_OFFSETS[randomSeedIndices[1]] + sdfCommonParams.randomMaskSeed,
    SDF_SHADER_TILE_ID_OFFSETS[randomSeedIndices[2]] + sdfCommonParams.randomMaskSeed,
    SDF_SHADER_TILE_ID_OFFSETS[randomSeedIndices[3]] + sdfCommonParams.randomMaskSeed,
  );

  uniforms.sdfRepeat = v2Uniform(
    Math.ceil(50 / shaderSdfPatterns[0].xDistance),
    Math.ceil(50 / shaderSdfPatterns[0].yDistance),
  );
  uniforms.sdfSlopeAxis = { value: sdfCommonParams.slopeAxis === "Z" ? 1 : 0 };

  const zSizes = shaderSdfPatterns.map((pattern) => zSizeFitRounding(pattern));

  if (useSdfSIMD) {
    uniforms.sdfOffsetX = v4Uniform(
      shaderSdfPatterns[0].xOffset,
      shaderSdfPatterns[1].xOffset,
      shaderSdfPatterns[2].xOffset,
      shaderSdfPatterns[3].xOffset,
    );
    uniforms.sdfOffsetY = v4Uniform(
      getYOffsetForShader(shaderSdfPatterns[0], sdfCommonParams),
      getYOffsetForShader(shaderSdfPatterns[1], sdfCommonParams),
      getYOffsetForShader(shaderSdfPatterns[2], sdfCommonParams),
      getYOffsetForShader(shaderSdfPatterns[3], sdfCommonParams),
    );
    uniforms.sdfSizeX = v4Uniform(
      shaderSdfPatterns[0].enabledForShader ? shaderSdfPatterns[0].xSize * 0.5 : 0,
      shaderSdfPatterns[1].enabledForShader ? shaderSdfPatterns[1].xSize * 0.5 : 0,
      shaderSdfPatterns[2].enabledForShader ? shaderSdfPatterns[2].xSize * 0.5 : 0,
      shaderSdfPatterns[3].enabledForShader ? shaderSdfPatterns[3].xSize * 0.5 : 0,
    );
    uniforms.sdfSizeY = v4Uniform(
      shaderSdfPatterns[0].enabledForShader ? shaderSdfPatterns[0].ySize * 0.5 : 0,
      shaderSdfPatterns[1].enabledForShader ? shaderSdfPatterns[1].ySize * 0.5 : 0,
      shaderSdfPatterns[2].enabledForShader ? shaderSdfPatterns[2].ySize * 0.5 : 0,
      shaderSdfPatterns[3].enabledForShader ? shaderSdfPatterns[3].ySize * 0.5 : 0,
    );
    uniforms.sdfSizeZ = v4Uniform(
      shaderSdfPatterns[0].enabledForShader ? zSizes[0] * 0.5 : -uniforms.sdfDepth.value,
      shaderSdfPatterns[1].enabledForShader ? zSizes[1] * 0.5 : -uniforms.sdfDepth.value,
      shaderSdfPatterns[2].enabledForShader ? zSizes[2] * 0.5 : -uniforms.sdfDepth.value,
      shaderSdfPatterns[3].enabledForShader ? zSizes[3] * 0.5 : -uniforms.sdfDepth.value,
    );
    uniforms.sdfDistanceX = v4Uniform(
      shaderSdfPatterns[0].xDistance,
      shaderSdfPatterns[1].xDistance,
      shaderSdfPatterns[2].xDistance,
      shaderSdfPatterns[3].xDistance,
    );
    uniforms.sdfDistanceY = v4Uniform(
      shaderSdfPatterns[0].yDistance,
      shaderSdfPatterns[1].yDistance,
      shaderSdfPatterns[2].yDistance,
      shaderSdfPatterns[3].yDistance,
    );

    // "sdfBevels" values are precomputed to minimize the number of computations required in the shader.
    // Note that x and y are swapped here for artist convenience.
    uniforms.sdfBevelsX = v4Uniform(
      sdfBevelsXForShader(shaderSdfPatterns[0]),
      sdfBevelsXForShader(shaderSdfPatterns[1]),
      sdfBevelsXForShader(shaderSdfPatterns[2]),
      sdfBevelsXForShader(shaderSdfPatterns[3]),
    );
    uniforms.sdfBevelsY = v4Uniform(
      sdfBevelsYForShader(shaderSdfPatterns[0]),
      sdfBevelsYForShader(shaderSdfPatterns[1]),
      sdfBevelsYForShader(shaderSdfPatterns[2]),
      sdfBevelsYForShader(shaderSdfPatterns[3]),
    );
    uniforms.sdfBevelsZ = v4Uniform(
      sdfBevelsZForShader(shaderSdfPatterns[0]),
      sdfBevelsZForShader(shaderSdfPatterns[1]),
      sdfBevelsZForShader(shaderSdfPatterns[2]),
      sdfBevelsZForShader(shaderSdfPatterns[3]),
    );
  } else {
    uniforms.sdfOffset = v2Uniform(
      shaderSdfPatterns[0].xOffset,
      getYOffsetForShader(shaderSdfPatterns[0], sdfCommonParams),
    );
    uniforms.sdfOffset2 = v2Uniform(
      shaderSdfPatterns[1].xOffset,
      getYOffsetForShader(shaderSdfPatterns[1], sdfCommonParams),
    );
    uniforms.sdfOffset3 = v2Uniform(
      shaderSdfPatterns[2].xOffset,
      getYOffsetForShader(shaderSdfPatterns[2], sdfCommonParams),
    );
    uniforms.sdfOffset4 = v2Uniform(
      shaderSdfPatterns[3].xOffset,
      getYOffsetForShader(shaderSdfPatterns[3], sdfCommonParams),
    );
    uniforms.sdfSize = v3Uniform(shaderSdfPatterns[0].xSize * 0.5, shaderSdfPatterns[0].ySize * 0.5, zSizes[0] * 0.5);
    uniforms.sdfSize2 = v3Uniform(shaderSdfPatterns[1].xSize * 0.5, shaderSdfPatterns[1].ySize * 0.5, zSizes[1] * 0.5);
    uniforms.sdfSize3 = v3Uniform(shaderSdfPatterns[2].xSize * 0.5, shaderSdfPatterns[2].ySize * 0.5, zSizes[2] * 0.5);
    uniforms.sdfSize4 = v3Uniform(shaderSdfPatterns[3].xSize * 0.5, shaderSdfPatterns[3].ySize * 0.5, zSizes[3] * 0.5);
    uniforms.sdfDistance = v2Uniform(shaderSdfPatterns[0].xDistance, shaderSdfPatterns[0].yDistance);
    uniforms.sdfDistance2 = v2Uniform(shaderSdfPatterns[1].xDistance, shaderSdfPatterns[1].yDistance);
    uniforms.sdfDistance3 = v2Uniform(shaderSdfPatterns[2].xDistance, shaderSdfPatterns[2].yDistance);
    uniforms.sdfDistance4 = v2Uniform(shaderSdfPatterns[3].xDistance, shaderSdfPatterns[3].yDistance);

    // "sdfBevels" values are precomputed to minimize the number of computations required in the shader.
    // Note that x and y are swapped here for artist convenience.
    uniforms.sdfBevels = v3Uniform(
      sdfBevelsXForShader(shaderSdfPatterns[0]),
      sdfBevelsYForShader(shaderSdfPatterns[0]),
      sdfBevelsZForShader(shaderSdfPatterns[0]),
    );
    uniforms.sdfBevels2 = v3Uniform(
      sdfBevelsXForShader(shaderSdfPatterns[1]),
      sdfBevelsYForShader(shaderSdfPatterns[1]),
      sdfBevelsZForShader(shaderSdfPatterns[1]),
    );
    uniforms.sdfBevels3 = v3Uniform(
      sdfBevelsXForShader(shaderSdfPatterns[2]),
      sdfBevelsYForShader(shaderSdfPatterns[2]),
      sdfBevelsZForShader(shaderSdfPatterns[2]),
    );
    uniforms.sdfBevels4 = v3Uniform(
      sdfBevelsXForShader(shaderSdfPatterns[3]),
      sdfBevelsYForShader(shaderSdfPatterns[3]),
      sdfBevelsZForShader(shaderSdfPatterns[3]),
    );
  }
  uniforms.sdfSlope = v4Uniform(
    degreeToRadian(shaderSdfPatterns[0].slope),
    degreeToRadian(shaderSdfPatterns[1].slope),
    degreeToRadian(shaderSdfPatterns[2].slope),
    degreeToRadian(shaderSdfPatterns[3].slope),
  );
  uniforms.sdfOffsetZ = v4Uniform(
    getZOffsetForShader(minZ, shaderSdfPatterns[0], sdfCommonParams),
    getZOffsetForShader(minZ, shaderSdfPatterns[1], sdfCommonParams),
    getZOffsetForShader(minZ, shaderSdfPatterns[2], sdfCommonParams),
    getZOffsetForShader(minZ, shaderSdfPatterns[3], sdfCommonParams),
  );

  const roundStrength = new THREE.Vector4(0, 0, 0, 0);
  for (let index = 0; index < 4; ++index) {
    if (shaderSdfPatterns[index].roundSidesMode !== "none") {
      roundStrength.setComponent(index, shaderSdfPatterns[index].roundStrength);
    }
  }

  uniforms.roundStrength = {
    type: "v4",
    value: roundStrength,
  };
  uniforms.varyHeightStr = v4Uniform(
    shaderSdfPatterns[0].varyHeight ? shaderSdfPatterns[0].varyHeightScale : 0,
    shaderSdfPatterns[1].varyHeight ? shaderSdfPatterns[1].varyHeightScale : 0,
    shaderSdfPatterns[2].varyHeight ? shaderSdfPatterns[2].varyHeightScale : 0,
    shaderSdfPatterns[3].varyHeight ? shaderSdfPatterns[3].varyHeightScale : 0,
  );
  uniforms.sdfRotation = v4Uniform(
    degreeToRadian(shaderSdfPatterns[0].rotation),
    degreeToRadian(shaderSdfPatterns[1].rotation),
    degreeToRadian(shaderSdfPatterns[2].rotation),
    degreeToRadian(shaderSdfPatterns[3].rotation),
  );
  uniforms.cutXfromYStr = v4Uniform(
    shaderSdfPatterns[0].cutXfromY ? shaderSdfPatterns[0].cutXfromYScale : 0,
    shaderSdfPatterns[1].cutXfromY ? shaderSdfPatterns[1].cutXfromYScale : 0,
    shaderSdfPatterns[2].cutXfromY ? shaderSdfPatterns[2].cutXfromYScale : 0,
    shaderSdfPatterns[3].cutXfromY ? shaderSdfPatterns[3].cutXfromYScale : 0,
  );
  return depthForMaskMultiplier;
}
